import React, { Component } from 'react';
import { toDecimals } from '../../helpers/currency';
import { throttle } from '@showclix-shared-util/throttle';

class Marketing extends Component {
  expiredCartTimeout = null;
  localStoragePrefix = '';
  reportAddPaymentInfo = true;

  constructor(props) {
    super(props);

    this.state = {
      canSendExpiredEvent: true,
      lastInsuranceEvent: null,
    }
  }

  componentWillUnmount() {
    this.throttledReportCartExpiredEvent.clearTimeout();
  }

  render() {
    return null;
  }

  onCouponSubmitted = (code) => {
    this.reportWholeReservationEvent('couponSubmitted', { coupons: [{ couponCode: code }]});
  }

  onReservationStart = () => {
    this.reportWholeReservationEvent('reservationCreated');
  }

  onReservationComplete = () => {
    const reservationSessionKey = this.localStoragePrefix + this.props.confirmationNumber;
    const sent = sessionStorage.getItem(reservationSessionKey);
    if (!sent) {
      // add_payment_info event
      if (Array.isArray(this.props.payments) && this.props.payments.length) {
        const payment = this.props.payments[0];
        if (this.reportAddPaymentInfo && payment?.payment_method) {
          this.reportWholeReservationEvent('addPaymentInfo', {
            paymentItems: {
              payment_type: payment.payment_method,
            }
          });
        }
      }

      // purchase event
      this.reportWholeReservationEvent('purchase', {
        paymentItems: {
          orderId: this.props.confirmationNumber,
        }
      });

      sessionStorage.setItem(reservationSessionKey,'1');
    }
  }

  onTicketsAdded = tickets => {
    this.reportUpdateCartItemsEvent('addToCart', 'ticket', tickets);
  }

  onProductsAdded = products => {
    this.reportUpdateCartItemsEvent('addToCart', 'product', products);
  }

  onBundlesAdded = bundles => {
    this.reportUpdateCartItemsEvent('addToCart', 'bundle', bundles);
  }

  onTicketsRemoved = tickets => {
    this.reportUpdateCartItemsEvent('removeFromCart', 'ticket', tickets);
  }

  onProductsRemoved = products => {
    this.reportUpdateCartItemsEvent('removeFromCart', 'product', products);
  }

  onBundlesRemoved = bundles => {
    this.reportUpdateCartItemsEvent('removeFromCart', 'bundle', bundles);
  }

  onInsuranceOptionChanged = ({ event }) => {
    const eventName = event === 'accepted' ? 'insuranceAccepted' : 'insuranceDeclined';

    if (this.state.lastInsuranceEvent === event) {
      return;
    }
      
    if (this.state.lastInsuranceEvent) {
      this.reportEvent('insuranceChanged', {
        from: this.state.lastInsuranceEvent,
        to: event 
      })
    } else {
      this.reportEvent(eventName);
    }

    this.setState({
      lastInsuranceEvent: event
    });
  }

  onReservationClear = (_, reason) => {
    if (reason === 'expired') {
      this.throttledReportCartExpiredEvent();
    }

    if (reason === 'clearCart') {
      this.reportEvent('clearCart');
    }
  }

  onReservationExpiresIn = (expiresIn) => {
    if (expiresIn === 60) {
      this.reportEvent('expiresIn', { expiresIn });
    }
  }

  onCartTimerExtended = () => {
    this.reportWholeReservationEvent('cartTimerExtended');
  }

  reportCartExpiredEvent = () => this.reportEvent('cartExpired');

  throttledReportCartExpiredEvent = throttle(this.reportCartExpiredEvent, 3000);

  reportUpdateCartItemsEvent = (eventType, itemType, items) => {
    const itemsCount = [];
    items.forEach(item => {
      if(!itemsCount[this.itemKey(item, itemType)]) {
        itemsCount[this.itemKey(item, itemType)] = 0;
      }
      itemsCount[this.itemKey(item, itemType)]++;
    });

    let sellableItems = [];
    items.forEach(item => {
      let key = this.itemKey(item, itemType);
      if(itemsCount[key]) {
        sellableItems.push(this.formatItem(item, itemType, itemsCount));
        delete itemsCount[key];
      }
    });
    this.reportEvent(eventType, {
      currency: this.props.currency,
      value: this.getTotalCostOfItems(items),
      sellableItems: sellableItems
    });
  }

  reportWholeReservationEvent = (eventType, extraFields = {}) => {
    this.reportEvent(eventType, {
      currency: this.props.currency,
      value: toDecimals({amount: this.props.orderTotal, currency: this.props.currency}),
      sellableItems: this.formatAllReservationItems(),
      ...extraFields,
    });
  }

  formatAllReservationItems = () => {
    const items = [];
    const itemsCount = [];

    const updateItemsCount = (target, type) => {
      target.forEach(item => {
        const key = this.itemKey(item, type);

        if (!itemsCount[key]) {
          itemsCount[key] = 0;
        }

        itemsCount[key]++;
      });
    }

    const updateItems = (target, type) => {
      target.forEach(item => {
        let key = this.itemKey(item, type);
        if(itemsCount[key]) {
          items.push(this.formatItem(item, type, itemsCount));
          delete itemsCount[key];
        }
      });
    }

    updateItemsCount(this.props.tickets, 'ticket');
    updateItemsCount(this.props.productSales, 'product');
    updateItemsCount(this.props.bundleSales, 'bundle');

    updateItems(this.props.tickets, 'ticket');
    updateItems(this.props.productSales, 'product');
    updateItems(this.props.bundleSales, 'bundle');

    return items;
  }

  onStateTransition = page => {
    if (page === 'cart') {
      this.reportWholeReservationEvent('viewCart');
    } else if (typeof page === 'string' && page.substring(0, 9) === 'checkout-' && this.props.inLastStep) {
      // checkout step seen action
      this.reportWholeReservationEvent('beginCheckout');
    } else {
      this.reportEvent('pageView', {
        page: page
      });
    }
  }

  formatItem = (item, type, itemCounts) => {
    const {itemIdFieldMap, itemNameFieldMap} = this.itemMaps();
    let key = this.itemKey(item, type);

    return {
      sellableID: item?.[itemIdFieldMap[type]] + '',
      sellableName: item?.[itemNameFieldMap[type]] + '',
      sellableType: type,
      price: toDecimals({amount: +item?.cost?.amount + +item?.buyer_fee?.amount, currency: this.props.currency}),
      quantity: itemCounts[key] ? itemCounts[key] : 1,
    };
  }

  itemKey = (item, type) => {
    const {itemIdFieldMap, itemNameFieldMap} = this.itemMaps();
    return item?.[itemIdFieldMap[type]] + '-' + type + '-' + toDecimals({amount: +item?.cost?.amount + +item?.buyer_fee?.amount, currency: this.props.currency})
  }

  itemMaps() {
    const itemIdFieldMap = {
      ticket: 'price_level_id',
      product: 'product_id',
      bundle: 'bundle_id',
    };

    const itemNameFieldMap = {
      ticket: 'price_level_name',
      product: 'product_name',
      bundle: 'name',
    };
    return {itemIdFieldMap, itemNameFieldMap};
  }

  getTotalCostOfItems = items => {
    let cost = 0;
    if (Array.isArray(items)) {
      items.forEach(item => {
        cost += +(item?.cost?.amount ?? 0);
        cost += +(item?.buyer_fee?.amount ?? 0);
      });
    }

    return toDecimals({amount: cost, currency: this.props.currency});
  }
}

Marketing.propTypes = {
}

export default Marketing;
